// Game-tracker "Custom time" entry helpers (OTC-585).
//
// Storage stays canonical HH:MM:SS (unchanged backend + community API + history),
// but the UI lets a game master type digits POS-style with the colon auto-placed
// and an hours segment appearing only once minutes overflow:
//   5653   -> 56:53
//   12322  -> 01:23:22   (expands to hours when needed)
//
// These functions convert at the UI edges only.

// Live input transform. Strips to digits, drops insignificant leading zeros so
// the field can't corrupt itself when its own output is re-typed, caps at 6
// digits (HH:MM:SS), then groups right-to-left into pairs. Trailing groups are
// always two digits; the leading group is left unpadded so the longest valid
// string stays 8 chars ("XX:XX:XX") — that lets a maxlength="8" on the field
// reject overflow natively. Blur normalizes to the padded form via
// formatCustomTime.
//   "5653"  -> "56:53"
//   "12322" -> "1:23:22"   (blur -> "01:23:22")
//   "565"   -> "5:65"      (transient while typing; seconds settle as more is typed)
export function groupTimeDigits(value) {
  if (value == null) return '';
  let digits = String(value).replace(/\D/g, '').replace(/^0+/, '').slice(0, 6);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return digits;

  const groups = [];
  while (digits.length > 2) {
    groups.unshift(digits.slice(-2));
    digits = digits.slice(0, -2);
  }
  groups.unshift(digits);
  return groups.join(':');
}

// Display a stored value (HH:MM:SS, MM:SS, or with a trailing ".000") using the
// minimal grouping: a leading zero hours segment is dropped, an all-zero value
// renders blank.
//   "00:56:53" -> "56:53"
//   "01:23:22" -> "01:23:22"
//   "00:00:00" -> ""
export function formatCustomTime(stored) {
  if (!stored) return '';
  const s = String(stored).trim().split('.')[0];
  const parts = s.split(':');
  if (parts.length < 2) return '';
  const nums = parts.map(p => parseInt(p, 10));
  if (nums.some(n => isNaN(n))) return '';
  while (nums.length < 3) nums.unshift(0);
  const [h, m, sec] = nums.slice(-3);
  if (!h && !m && !sec) return '';
  const pad = n => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

// Normalize an entered/display value back to canonical HH:MM:SS for storage.
// Segment overflow carries upward, microwave-style, so the result is always a
// valid time — otherwise entries like "1:99" would reach the backend as
// "00:01:99" and be recorded as null.
//   "56:53"    -> "00:56:53"
//   "01:23:22" -> "01:23:22"
//   "1:99"     -> "00:02:39"
//   ""         -> ""
export function toStoredCustomTime(display) {
  if (!display) return '';
  const grouped = groupTimeDigits(display);
  if (!grouped) return '';
  const parts = grouped.split(':').map(p => parseInt(p, 10) || 0);
  while (parts.length < 3) parts.unshift(0);
  const [h, m, sec] = parts.slice(-3);
  const totalSeconds = h * 3600 + m * 60 + sec;
  const pad = n => String(n).padStart(2, '0');
  return `${pad(Math.floor(totalSeconds / 3600))}:${pad(Math.floor((totalSeconds % 3600) / 60))}:${pad(totalSeconds % 60)}`;
}
